import { useAuthContext, OrganizationWithRole } from '@/contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, Building2, ChevronDown, Check } from 'lucide-react';

export function WorkspaceSwitcher() {
  const {
    user,
    userOrganizations,
    currentWorkspace,
    switchToPersonalWorkspace,
    switchToOrganizationWorkspace,
  } = useAuthContext();

  const isPersonalWorkspace = currentWorkspace.type === 'personal';

  const getWorkspaceDisplayName = () => {
    if (isPersonalWorkspace) {
      return 'Personal';
    }
    return currentWorkspace.organizationName || 'Organization';
  };

  const getCurrentOrgAvatar = () => {
    if (isPersonalWorkspace) {
      return null;
    }
    const org = userOrganizations.find(o => o.id === currentWorkspace.organizationId);
    return org?.avatar_url;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between gap-2 px-3"
          data-testid="button-workspace-switcher"
        >
          <div className="flex items-center gap-2 min-w-0">
            {isPersonalWorkspace ? (
              <Avatar className="h-6 w-6">
                <AvatarImage src={user?.profileImageUrl || undefined} />
                <AvatarFallback>
                  <User className="h-3 w-3" />
                </AvatarFallback>
              </Avatar>
            ) : (
              <Avatar className="h-6 w-6">
                <AvatarImage src={getCurrentOrgAvatar() || undefined} />
                <AvatarFallback>
                  <Building2 className="h-3 w-3" />
                </AvatarFallback>
              </Avatar>
            )}
            <span className="truncate text-sm font-medium" data-testid="text-current-workspace">
              {getWorkspaceDisplayName()}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56" data-testid="dropdown-workspace-menu">
        <DropdownMenuLabel>Switch Workspace</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <DropdownMenuItem
          onClick={switchToPersonalWorkspace}
          className="flex items-center justify-between gap-2 cursor-pointer"
          data-testid="menu-item-personal-workspace"
        >
          <div className="flex items-center gap-2">
            <Avatar className="h-6 w-6">
              <AvatarImage src={user?.profileImageUrl || undefined} />
              <AvatarFallback>
                <User className="h-3 w-3" />
              </AvatarFallback>
            </Avatar>
            <span>Personal</span>
          </div>
          {isPersonalWorkspace && <Check className="h-4 w-4" data-testid="icon-check-personal" />}
        </DropdownMenuItem>
        
        {userOrganizations.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Organizations
            </DropdownMenuLabel>
            {userOrganizations.map((org: OrganizationWithRole) => {
              const isSelected = currentWorkspace.type === 'organization' && 
                currentWorkspace.organizationId === org.id;
              return (
                <DropdownMenuItem
                  key={org.id}
                  onClick={() => switchToOrganizationWorkspace(org)}
                  className="flex items-center justify-between gap-2 cursor-pointer"
                  data-testid={`menu-item-org-${org.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={org.avatar_url || undefined} />
                      <AvatarFallback>
                        <Building2 className="h-3 w-3" />
                      </AvatarFallback>
                    </Avatar>
                    <span className="truncate">{org.name}</span>
                  </div>
                  {isSelected && <Check className="h-4 w-4 shrink-0" data-testid={`icon-check-org-${org.id}`} />}
                </DropdownMenuItem>
              );
            })}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
